import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  courseFormSchema,
  CourseFormData,
} from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import {
  courseToFormData,
  emptyCourseFormData,
  formDataToPayload,
} from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/mapper";
import { fetchCourseMetadataAction } from "@/modules/learningCenter/features/CourseManagement/slices/courseListSlice";
import {
  publishCourseAction,
  archiveCourseAction,
} from "@/modules/learningCenter/features/CourseManagement/slices/courseListSlice";
import {
  getCourseAction,
  updateCourseAction,
  resetCreateUpdateCourseState,
} from "@/modules/learningCenter/features/CourseManagement/slices/createUpdateCourseSlice";

export const useEditCourse = (uuid: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const metadata = useAppSelector(
    (state: RootState) => state.courseList.metadata,
  );
  const course = useAppSelector(
    (state: RootState) => state.createUpdateCourse.fetchedCourse,
  );
  const loading = useAppSelector(
    (state: RootState) => state.createUpdateCourse.loadingGet,
  );
  const error = useAppSelector(
    (state: RootState) => state.createUpdateCourse.getError,
  );
  const isSubmitting = useAppSelector(
    (state: RootState) => state.createUpdateCourse.loadingUpdate,
  );

  const form = useForm<CourseFormData>({
    resolver: zodResolver(courseFormSchema),
    defaultValues: emptyCourseFormData(),
    mode: "onChange",
  });

  useEffect(() => {
    dispatch(fetchCourseMetadataAction());
    dispatch(getCourseAction(uuid));
    return () => {
      dispatch(resetCreateUpdateCourseState());
    };
  }, [dispatch, uuid]);

  useEffect(() => {
    if (course && course.uuid === uuid) {
      form.reset(courseToFormData(course));
    }
  }, [course, uuid, form]);

  const submitForm = form.handleSubmit((data) => {
    dispatch(
      updateCourseAction(uuid, formDataToPayload(data), (updated) => {
        showNotification(
          "Course saved",
          `"${updated.title}" was updated.`,
          "success",
        );
        dispatch(getCourseAction(uuid));
      }),
    );
  });

  const handleCancel = () => {
    navigate("/admin/learning-center/courses");
  };

  const handlePublish = () => {
    dispatch(
      publishCourseAction(uuid, () => {
        showNotification(
          "Course published",
          "The course is now published.",
          "success",
        );
        dispatch(getCourseAction(uuid));
      }),
    );
  };

  const handleArchive = () => {
    dispatch(
      archiveCourseAction(uuid, () => {
        showNotification(
          "Course archived",
          "The course has been archived.",
          "success",
        );
        navigate("/admin/learning-center/courses");
      }),
    );
  };

  return {
    form,
    metadata,
    course,
    loading,
    error,
    isSubmitting,
    submitForm,
    handleCancel,
    handlePublish,
    handleArchive,
  };
};
